<template>
    <Teleport to="body">
        <Transition name="custom-modal-fade">
            <div v-if="modelValue"
                 class="custom-modal-overlay"
                 @mousedown.self="onBackdropClick">
                <div ref="dialog"
                     class="custom-modal"
                     :class="'custom-modal--' + size"
                     role="dialog"
                     aria-modal="true"
                     :aria-labelledby="title ? titleId : null"
                     tabindex="-1"
                     @keydown="onKeydown">

                    <div v-if="title || showClose" class="custom-modal__header">
                        <h2 v-if="title" :id="titleId" class="pstitle custom-modal__title">{{ title }}</h2>
                        <button v-if="showClose"
                                type="button"
                                class="custom-modal__close"
                                aria-label="Close"
                                @click="close">
                            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                                <line x1="2" y1="2" x2="10" y2="10" stroke="black" stroke-width="2" />
                                <line x1="2" y1="10" x2="10" y2="2" stroke="black" stroke-width="2" />
                            </svg>
                        </button>
                    </div>

                    <div class="custom-modal__body">
                        <slot></slot>
                    </div>

                    <div v-if="$slots.footer" class="custom-modal__footer">
                        <slot name="footer" :close="close"></slot>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
let uid = 0;

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default {
    name: "ModalCard",
    props: {
        modelValue: { type: Boolean, default: false },
        title: { type: String, default: "" },
        size: {
            type: String,
            default: "md",
            validator: (value) => ["sm", "md", "lg"].includes(value),
        },
        showClose: { type: Boolean, default: true },
        closeOnBackdrop: { type: Boolean, default: true },
        closeOnEsc: { type: Boolean, default: true },
    },
    emits: ["update:modelValue", "close"],
    data() {
        return {
            titleId: "custom-modal-title-" + (++uid),
            previouslyFocused: null,
        };
    },
    watch: {
        modelValue: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) {
                    this.onOpen();
                } else {
                    this.onClose();
                }
            },
        },
    },
    beforeUnmount() {
        // If the parent page goes away while the modal is open, don't leave the page unscrollable
        if (this.modelValue) {
            document.body.classList.remove("custom-modal-open");
        }
    },
    methods: {
        close() {
            this.$emit("update:modelValue", false);
            this.$emit("close");
        },

        onOpen() {
            this.previouslyFocused = document.activeElement;
            document.body.classList.add("custom-modal-open");
            this.$nextTick(() => {
                if (this.$refs.dialog) {
                    this.$refs.dialog.focus();
                }
            });
        },

        onClose() {
            document.body.classList.remove("custom-modal-open");
            // Return focus to whatever opened the modal
            if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
                this.previouslyFocused.focus();
            }
            this.previouslyFocused = null;
        },

        onBackdropClick() {
            if (this.closeOnBackdrop) {
                this.close();
            }
        },

        onKeydown(e) {
            if (e.key === "Escape" && this.closeOnEsc) {
                e.stopPropagation();
                this.close();
                return;
            }
            if (e.key !== "Tab") {
                return;
            }

            // Keep Tab / Shift+Tab inside the modal
            const focusable = Array.from(this.$refs.dialog.querySelectorAll(FOCUSABLE));
            if (focusable.length === 0) {
                e.preventDefault();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (e.shiftKey && (active === first || active === this.$refs.dialog)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        },
    },
};
</script>

<style lang="scss">
body.custom-modal-open {
    overflow: hidden;
}

.custom-modal-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1050;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.5);
}

.custom-modal {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: calc(100vh - 2rem);
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.25);
    outline: none;

    &--sm { max-width: 400px; }
    &--md { max-width: 600px; }
    &--lg { max-width: 900px; }
}

.custom-modal__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem 1.5rem 0;
}

.custom-modal__title {
    margin: 0;
}

.custom-modal__close {
    display: flex;
    align-items: center;
    padding: 0.25rem;
    margin-left: auto;
    background: none;
    border: 0;
    cursor: pointer;
    opacity: 0.6;

    &:hover,
    &:focus-visible {
        opacity: 1;
    }
}

.custom-modal__body {
    padding: 1.5rem;
    overflow-y: auto;
}

.custom-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0 1.5rem 1.5rem;
}

// Fade the backdrop and slide the dialog up slightly
.custom-modal-fade-enter-active,
.custom-modal-fade-leave-active {
    transition: opacity 0.2s ease;

    .custom-modal {
        transition: transform 0.2s ease;
    }
}

.custom-modal-fade-enter-from,
.custom-modal-fade-leave-to {
    opacity: 0;

    .custom-modal {
        transform: translateY(1rem);
    }
}

@media (prefers-reduced-motion: reduce) {
    .custom-modal-fade-enter-active,
    .custom-modal-fade-leave-active,
    .custom-modal-fade-enter-active .custom-modal,
    .custom-modal-fade-leave-active .custom-modal {
        transition: none;
    }
}
</style>